import type {MCFetcher} from '@github-ui/ahp-relay/mission-control/mission-control-environment-client'
import {FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'

const ACTIVATION_TRACE_ID_PATTERN = /^[0-9a-f]{32}$/i
const DATADOG_TRACE_BASE_URL = 'https://app.datadoghq.com/apm/trace/'
const ACTIVATION_TRACE_STORAGE_KEY = 'github.activation-trace-ids'
const EMPTY_TRACE_ID = ''
const MAX_TASKS = 5

const traceIdsByTask = new Map<string, string>()
const listenersByTask = new Map<string, Set<() => void>>()

interface StoredActivationTrace {
  taskId: string
  traceId: string
}

function getSessionStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage
  } catch {
    return undefined
  }
}

function readStoredActivationTraces(): StoredActivationTrace[] {
  const storage = getSessionStorage()
  if (!storage) return []

  try {
    const value: unknown = JSON.parse(storage.getItem(ACTIVATION_TRACE_STORAGE_KEY) ?? '[]')
    if (!Array.isArray(value)) return []
    return value
      .filter(
        (entry): entry is StoredActivationTrace =>
          !!entry &&
          typeof entry === 'object' &&
          'taskId' in entry &&
          typeof entry.taskId === 'string' &&
          entry.taskId.length > 0 &&
          'traceId' in entry &&
          typeof entry.traceId === 'string' &&
          ACTIVATION_TRACE_ID_PATTERN.test(entry.traceId),
      )
      .slice(-MAX_TASKS)
  } catch {
    return []
  }
}

function storedActivationTraceId(taskId: string): string | undefined {
  const traces = readStoredActivationTraces()
  for (let index = traces.length - 1; index >= 0; index--) {
    if (traces[index]?.taskId === taskId) return traces[index]?.traceId
  }
  return undefined
}

function storeActivationTraceId(taskId: string, traceId: string): void {
  const storage = getSessionStorage()
  if (!storage) return

  const traces = readStoredActivationTraces().filter(trace => trace.taskId !== taskId)
  traces.push({taskId, traceId})
  try {
    storage.setItem(ACTIVATION_TRACE_STORAGE_KEY, JSON.stringify(traces.slice(-MAX_TASKS)))
  } catch {
    // The in-memory trace remains available when same-tab handoff storage is unavailable.
  }
}

export function getActivationTraceIdSnapshot(taskId: string | undefined): string {
  if (!taskId) return EMPTY_TRACE_ID
  return traceIdsByTask.get(taskId) ?? storedActivationTraceId(taskId) ?? EMPTY_TRACE_ID
}

export function subscribeToActivationTraceId(taskId: string | undefined, listener: () => void): () => void {
  if (!taskId) return () => {}
  const listeners = listenersByTask.get(taskId) ?? new Set<() => void>()
  listeners.add(listener)
  listenersByTask.set(taskId, listeners)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) listenersByTask.delete(taskId)
  }
}

export function recordActivationTraceId(taskId: string, traceId: unknown): void {
  if (typeof traceId !== 'string' || !ACTIVATION_TRACE_ID_PATTERN.test(traceId)) return
  storeActivationTraceId(taskId, traceId)
  if (traceIdsByTask.get(taskId) === traceId) return
  traceIdsByTask.delete(taskId)
  traceIdsByTask.set(taskId, traceId)

  for (const staleTaskId of [...traceIdsByTask.keys()].slice(0, Math.max(0, traceIdsByTask.size - MAX_TASKS))) {
    if (!listenersByTask.has(staleTaskId)) traceIdsByTask.delete(staleTaskId)
  }

  for (const listener of listenersByTask.get(taskId) ?? []) listener()
}

const TRACEPARENT_VERSION = '00'
const TRACEPARENT_TRACE_ID_PATTERN = /^[0-9a-f]{32}$/i
const TRACEPARENT_PARENT_ID_PATTERN = /^[0-9a-f]{16}$/i
const TRACEPARENT_FLAGS_PATTERN = /^[0-9a-f]{2}$/i
const ALL_ZERO_TRACE_ID = /^0+$/
const CAPTURE_STATUSES = new Set([200, 202, 409])

export function parseTraceIdFromTraceparent(traceparent: unknown): string | undefined {
  if (typeof traceparent !== 'string') return undefined
  const parts = traceparent.split('-')
  if (parts.length !== 4) return undefined
  const [version, traceId, parentId, flags] = parts as [string, string, string, string]
  if (version !== TRACEPARENT_VERSION) return undefined
  if (!TRACEPARENT_TRACE_ID_PATTERN.test(traceId) || ALL_ZERO_TRACE_ID.test(traceId)) return undefined
  if (!TRACEPARENT_PARENT_ID_PATTERN.test(parentId) || ALL_ZERO_TRACE_ID.test(parentId)) return undefined
  if (!TRACEPARENT_FLAGS_PATTERN.test(flags)) return undefined
  return traceId.toLowerCase()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

// activation_context reflects the current activation across task creation and environment startup.
export function activationTraceIdFromResponseBody(body: unknown): string | undefined {
  if (!isRecord(body)) return undefined
  const activationContext = body.activation_context
  return isRecord(activationContext) ? parseTraceIdFromTraceparent(activationContext.traceparent) : undefined
}

async function captureActivationTraceId(taskId: string, response: Response): Promise<void> {
  if (!CAPTURE_STATUSES.has(response.status)) return

  let body: unknown
  try {
    body = await response.clone().json()
  } catch {
    return
  }

  const traceId = activationTraceIdFromResponseBody(body)
  if (traceId) recordActivationTraceId(taskId, traceId)
}

async function captureActivationTraceIdWithoutAffectingRequest(taskId: string, response: Response): Promise<void> {
  try {
    await captureActivationTraceId(taskId, response)
  } catch {
    // Trace capture is diagnostic only and must never replace the relay result.
  }
}

export function createActivationTraceCapturingFetcher(fetcher: MCFetcher, taskId: string): MCFetcher {
  return async (path, options) => {
    try {
      const response = await fetcher(path, options)
      await captureActivationTraceIdWithoutAffectingRequest(taskId, response)
      return response
    } catch (error) {
      if (error instanceof FetchCAPIError && error.response) {
        await captureActivationTraceIdWithoutAffectingRequest(taskId, error.response)
      }
      throw error
    }
  }
}

export function activationTraceUrl(traceId: string): string | undefined {
  if (!ACTIVATION_TRACE_ID_PATTERN.test(traceId)) return undefined
  const url = new URL(encodeURIComponent(traceId), DATADOG_TRACE_BASE_URL)
  return url.toString()
}

export function clearActivationTraceIds(): void {
  const taskIds = new Set([...traceIdsByTask.keys(), ...readStoredActivationTraces().map(trace => trace.taskId)])
  traceIdsByTask.clear()
  try {
    getSessionStorage()?.removeItem(ACTIVATION_TRACE_STORAGE_KEY)
  } catch {
    // Test and cleanup helpers remain best effort when browser storage is unavailable.
  }
  for (const taskId of taskIds) {
    for (const listener of listenersByTask.get(taskId) ?? []) listener()
  }
}
